import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, KeyRound, Ban, CheckCircle, Trash2, UserPlus } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const ROLE_LABELS = { super_admin: "Super-admin", admin: "Admin", editor: "Éditeur", moderator: "Modérateur", user: "Utilisateur" };
const STATUS_LABELS = { active: "Actif", suspendu: "Suspendu", desactive: "Désactivé" };

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState(null);
    const [search, setSearch] = useState("");
    const [inviteOpen, setInviteOpen] = useState(false);
    const [invite, setInvite] = useState({ email: "", name: "", role: "user" });
    const [setupLink, setSetupLink] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = () => api.get("/admin/users", { params: { search } }).then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const act = async (fn, successMsg) => {
        try {
            const res = await fn();
            toast.success(successMsg);
            load();
            return res;
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const changeRole = (u, role) => act(() => api.patch(`/admin/users/${u.user_id}/role`, { role }), `Rôle de ${u.email} mis à jour`);
    const changeStatus = (u, status) => act(() => api.post(`/admin/users/${u.user_id}/status`, { status }), `Statut de ${u.email} : ${STATUS_LABELS[status]}`);
    const revoke = (u) => act(() => api.post(`/admin/users/${u.user_id}/revoke-sessions`), `Sessions de ${u.email} révoquées`);
    const resetAccess = async (u) => {
        const res = await act(() => api.post(`/admin/users/${u.user_id}/reset-access`), "Lien de réinitialisation généré");
        if (res?.data?.reset_path) setSetupLink(`${window.location.origin}${res.data.reset_path}`);
    };
    const sendInvite = async () => {
        const res = await act(() => api.post("/admin/users/invite", invite), "Invitation créée");
        if (res?.data?.setup_path) {
            setSetupLink(`${window.location.origin}${res.data.setup_path}`);
            setInviteOpen(false);
            setInvite({ email: "", name: "", role: "user" });
        }
    };

    return (
        <div data-testid="admin-users">
            <PageHeader title="Utilisateurs du site" subtitle="Comptes du site web uniquement — sans aucun lien avec les comptes de l'application mobile.">
                <Button onClick={() => setInviteOpen(true)} className="bg-forest text-cream gap-2" data-testid="invite-user-button"><UserPlus size={16} /> Inviter</Button>
            </PageHeader>
            <div className="flex gap-3 mb-5">
                <Input placeholder="Rechercher (nom, e-mail)…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-xs bg-white" data-testid="users-search-input" />
                <Button variant="outline" onClick={load} data-testid="users-search-button">Rechercher</Button>
            </div>
            <div className="rounded-2xl bg-white border border-gold/25 shadow-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-pineneedle border-b">
                            <th className="p-4">Compte</th><th className="p-4">Rôle</th><th className="p-4">Statut</th><th className="p-4">Dernière connexion</th><th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(users || []).map((u) => (
                            <tr key={u.user_id} className="border-b last:border-0" data-testid={`user-row-${u.user_id}`}>
                                <td className="p-4">
                                    <p className="font-semibold text-forest">{u.name}</p>
                                    <p className="text-xs text-pineneedle">{u.email} · {(u.providers || []).join(", ")}</p>
                                </td>
                                <td className="p-4">
                                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.user_id === me.user_id}>
                                        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(ROLE_LABELS).map(([k, l]) => (
                                                <SelectItem key={k} value={k} disabled={k === "super_admin" && me.role !== "super_admin"}>{l}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="p-4">
                                    <span className={`text-xs px-2 py-1 rounded-full ${u.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`} data-testid={`status-${u.user_id}`}>
                                        {STATUS_LABELS[u.status] || u.status}
                                    </span>
                                </td>
                                <td className="p-4 text-xs text-pineneedle">{fmtDate(u.last_login_at)}</td>
                                <td className="p-4">
                                    <div className="flex gap-1.5 flex-wrap">
                                        {u.status === "active" ? (
                                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => changeStatus(u, "suspendu")} disabled={u.user_id === me.user_id} data-testid={`suspend-${u.user_id}`}><Ban size={13} /> Suspendre</Button>
                                        ) : (
                                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => changeStatus(u, "active")} data-testid={`activate-${u.user_id}`}><CheckCircle size={13} /> Activer</Button>
                                        )}
                                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => revoke(u)} data-testid={`revoke-${u.user_id}`}><ShieldAlert size={13} /> Sessions</Button>
                                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => resetAccess(u)} data-testid={`reset-${u.user_id}`}><KeyRound size={13} /> Réinitialiser</Button>
                                        <Button size="sm" variant="outline" className={`h-8 text-xs gap-1 ${u.is_test ? "border-amber-500 text-amber-700" : ""}`} onClick={() => act(() => api.post(`/admin/users/${u.user_id}/flag-test`), u.is_test ? "Compte démarqué test" : "Compte marqué comme test (exclu du compteur public)")} data-testid={`flag-test-${u.user_id}`} title="Exclure ce compte du compteur public">
                                            {u.is_test ? "Test ✓" : "Test"}
                                        </Button>
                                        {me.role === "super_admin" && u.user_id !== me.user_id && (
                                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" onClick={() => setDeleteTarget(u)} data-testid={`delete-${u.user_id}`}><Trash2 size={13} /></Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {users && users.length === 0 && <p className="p-8 text-center text-pineneedle text-sm">Aucun compte trouvé.</p>}
            </div>

            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent className="bg-cream" data-testid="invite-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Inviter un compte</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Nom</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name" /></div>
                        <div><Label>E-mail</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" /></div>
                        <div>
                            <Label>Rôle</Label>
                            <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["admin", "editor", "moderator", "user"].map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={sendInvite} className="w-full bg-forest text-cream" data-testid="invite-submit">Créer le compte et générer le lien d'activation</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!setupLink} onOpenChange={() => setSetupLink("")}>
                <DialogContent className="bg-cream" data-testid="setup-link-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Lien d'activation sécurisé</DialogTitle></DialogHeader>
                    <p className="text-sm text-pineneedle">Transmettez ce lien à l'utilisateur (valable 1 à 24 h) :</p>
                    <div className="flex gap-2">
                        <Input readOnly value={setupLink} className="text-xs" data-testid="setup-link-value" />
                        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(setupLink); toast.success("Lien copié"); }} data-testid="copy-setup-link">Copier</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-cream">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer {deleteTarget?.email} ?</AlertDialogTitle>
                        <AlertDialogDescription>Cette action est définitive : le compte et ses sessions seront supprimés.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-white" onClick={() => act(() => api.delete(`/admin/users/${deleteTarget.user_id}`), "Compte supprimé")} data-testid="delete-confirm">
                            Supprimer définitivement
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
